/**
 * The owner's private test page (/test) — Marko 16.9.2026: "testaus moodi pitää olla ihan julkisessa versiossa, se ei
 * edelleenkään ole kenenkään muun käytössä… jonkin linkki minulle, josta pääsen salaiselle test sivulle, jonne laitetaan
 * kaikki testaukseen liittyvät kuviot."
 *
 * It is in the public build but reachable only by the owner: /api/me marks that one account and the route behind these
 * buttons (/api/test/sim) answers 404 to everyone else and to any unit started without SNW_TEST_TOOLS=1. Nobody else can
 * see it, and nothing here changes the game — the simulated players are ordinary players a script taps for.
 *
 * Deliberately plain: this is a tool, not a view of the game, so it borrows none of the drawn UI (and adds no CSS to it).
 */
import { useEffect, useState } from 'preact/hooks';
import { route } from '../state/store';

type Status = { running: boolean; what: string | null; since: number | null; log: string[] };
const box = { background: '#0b1030', color: '#dbe9ff', border: '1px solid #2b3c7a', borderRadius: 14, padding: 16, marginBottom: 14 };
const btn = { background: '#17306e', color: '#fff', border: '1px solid #3f63c8', borderRadius: 999, padding: '12px 20px', font: '600 16px system-ui', cursor: 'pointer', marginRight: 10, marginTop: 8 };

export function TestPage() {
  const [target, setTarget] = useState('meet');
  const [code, setCode] = useState('');
  const [count, setCount] = useState(2);
  const [minutes, setMinutes] = useState(30);
  const [status, setStatus] = useState<Status | null>(null);
  const [err, setErr] = useState('');

  const load = async () => { try { const r = await fetch('/api/test/sim', { credentials: 'same-origin' }); if (r.ok) setStatus((await r.json()) as Status); } catch { /* the page polls again in a moment */ } };
  useEffect(() => { void load(); const t = setInterval(() => void load(), 3000); return () => clearInterval(t); }, []);

  const start = async () => {
    setErr('');
    const what = target === 'code' ? code.trim().toUpperCase() : target;
    const r = await fetch('/api/test/sim', { method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ target: what, count, minutes }) });
    if (!r.ok) setErr(r.status === 409 ? 'A run is already going — stop it first.' : r.status === 400 ? 'Give a six-character room code, or pick MEET / FLIRT.' : `Could not start (${r.status})`);
    void load();
  };
  const stop = async () => { await fetch('/api/test/sim', { method: 'DELETE', credentials: 'same-origin' }); void load(); };

  // the players print the room they landed in; the page turns that into a link to open on the phone
  const room = (status?.log ?? []).map((l) => l.match(/(?:table|room) ([A-Z0-9]{6})/)?.[1]).filter(Boolean).pop();

  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'auto', background: '#060a20', padding: 20, font: '400 16px/1.45 system-ui' }} data-testid="test-page">
      <h1 style={{ font: '700 22px system-ui', color: '#fff', margin: '4px 0 16px' }}>Test tools</h1>

      <div style={box}>
        <b style={{ color: '#fff' }}>Simulated players</b>
        <p style={{ margin: '8px 0 0' }}>They sign in as test accounts, play every phase at a human pace and join a rematch you propose. Their accounts are erased when the run ends.</p>
        <div style={{ marginTop: 10 }}>
          {(['meet', 'flirt', 'all', 'code'] as const).map((t) => (
            <button key={t} type="button" style={{ ...btn, background: target === t ? '#2a52c0' : '#17306e' }} onClick={() => setTarget(t)}>
              {t === 'code' ? 'My room code' : t === 'all' ? 'Any public table' : `Public ${t.toUpperCase()}`}
            </button>
          ))}
        </div>
        {target === 'code' && (
          <div style={{ marginTop: 10 }}>
            <input value={code} onInput={(e) => setCode((e.target as HTMLInputElement).value)} placeholder="ABC123" maxLength={6}
              style={{ background: '#050a22', color: '#fff', border: '1px solid #3f63c8', borderRadius: 10, padding: '10px 14px', font: '600 18px system-ui', letterSpacing: 2, width: 140 }} />
          </div>
        )}
        <div style={{ marginTop: 10 }}>
          Players: {[1, 2, 3, 4, 5].map((n) => <button key={n} type="button" style={{ ...btn, background: count === n ? '#2a52c0' : '#17306e', padding: '8px 14px' }} onClick={() => setCount(n)}>{n}</button>)}
        </div>
        <div style={{ marginTop: 10 }}>
          Minutes: {[15, 30, 45].map((n) => <button key={n} type="button" style={{ ...btn, background: minutes === n ? '#2a52c0' : '#17306e', padding: '8px 14px' }} onClick={() => setMinutes(n)}>{n}</button>)}
        </div>
        <div style={{ marginTop: 14 }}>
          <button type="button" style={{ ...btn, background: '#1f7a46', borderColor: '#2fbb6c' }} onClick={() => void start()} data-testid="test-sim-start">Start</button>
          <button type="button" style={{ ...btn, background: '#7a1f2f', borderColor: '#bb2f4a' }} onClick={() => void stop()} data-testid="test-sim-stop">Stop</button>
        </div>
        {err && <p style={{ color: '#ff9bb0', margin: '10px 0 0' }}>{err}</p>}
      </div>

      <div style={box}>
        <b style={{ color: '#fff' }}>Status</b>
        <p style={{ margin: '8px 0 0' }}>{status?.running ? `Running — ${status.what}` : 'Not running'}</p>
        {room && (
          <p style={{ margin: '10px 0 0' }}>
            They are in room <b style={{ color: '#fff' }}>{room}</b> — <a href={`/r/${room}`} style={{ color: '#7fd0ff' }}>open that table</a>
          </p>
        )}
        <pre style={{ marginTop: 10, maxHeight: 260, overflow: 'auto', background: '#050a22', border: '1px solid #22305f', borderRadius: 10, padding: 12, font: '400 13px/1.4 ui-monospace, monospace', color: '#b9cdf5', whiteSpace: 'pre-wrap' }}>
          {(status?.log ?? []).join('\n') || '—'}
        </pre>
      </div>

      <button type="button" style={btn} onClick={() => { history.pushState(null, '', '/'); route.value = { name: 'home' }; }}>Back to the Lounge</button>
    </div>
  );
}
